import React, { useContext, useEffect, useState } from "react";
import { ACCOUNT_TYPE_NORMAL, API_BASE_URL } from "@config/config";
import { AuthContext } from "@context/AuthContext";
import Button from "@components/Ui/Button/index.js";
import Loader from "@components/Ui/Loader";
import MovieTile from "@components/V3/Movies/MovieTile";
import "./style.scss";

const WatchList = () => {
    const { user } = useContext(AuthContext);
    const [watchlistMovies, setWatchlistMovies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCurrent, setShowCurrent] = useState(true);

    const isNormal = user?.account_type_id === ACCOUNT_TYPE_NORMAL;

    const nowShowingMovies = watchlistMovies.filter(movie => {
        if (!movie.release_date) return true;
        const releaseDate = new Date(movie.release_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return releaseDate < today;
    });

    const comingSoonMovies = watchlistMovies.filter(movie => {
        if (!movie.release_date) return false;
        const releaseDate = new Date(movie.release_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return releaseDate >= today;
    });

    const displayedMovies = showCurrent ? nowShowingMovies : comingSoonMovies;

    useEffect(() => {
        if (!isNormal) {
            setIsLoading(false);
            return;
        }

        fetchWatchlist();
    }, [isNormal]);

    const fetchWatchlist = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await fetch(`${API_BASE_URL}/watchlist`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch watchlist");
            }

            const data = await response.json();
            setWatchlistMovies(data.data || []);
        } catch (err) {
            console.error("Error fetching watchlist:", err);
            setError("Failed to load your watchlist. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleMovieRemovedFromWatchlist = (movieId) => {
        setWatchlistMovies(prevMovies =>
            prevMovies.filter(movie => movie.id !== movieId && movie.movie_id !== movieId)
        );
    };

    if (!isNormal) {
        return null;
    }

    if (isLoading) {
        return (
            <div className="watchlist">
                <h1 className="watchlist__title">My Watchlist</h1>
                <Loader isLoading={isLoading} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="watchlist">
                <div className="watchlist__header">
                    <h1 className="watchlist__title">My Watchlist</h1>
                </div>
                <div className="watchlist__error">
                    <p className="watchlist__error-message">{error}</p>
                    <button
                        className="watchlist__retry-button"
                        onClick={fetchWatchlist}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (watchlistMovies.length === 0) {
        return (
            <div className="watchlist">
                <div className="watchlist__header">
                    <h1 className="watchlist__title">My Watchlist</h1>
                </div>
                <div className="watchlist__empty">
                    <p className="watchlist__empty-message">
                        You haven&apos;t added any movies to your watchlist yet.
                    </p>
                    <p className="watchlist__empty-hint">
                        Click the heart icon on any movie to add it to your watchlist!
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="watchlist">
            <h1 className="watchlist__title">My Watchlist</h1>
            <div className="watchlist__header">
                <ul className="watchlist__buttons">
                    <li className="watchlist__item">
                        <Button
                            additionalClasses={`button watchlist__button ${
                                showCurrent ? "watchlist__button--selected" : ""
                            }`}
                            onClick={() => setShowCurrent(true)}
                        >
                            Now Showing
                        </Button>
                    </li>
                    <li className="watchlist__item">
                        <Button
                            additionalClasses={`button watchlist__button ${
                                !showCurrent ? "watchlist__button--selected" : ""
                            }`}
                            onClick={() => setShowCurrent(false)}
                        >
                            Coming Soon
                        </Button>
                    </li>
                </ul>
            </div>

            <div className="watchlist__content">
                {displayedMovies.length === 0 ? (
                    <div className="watchlist__empty">
                        <p className="watchlist__empty-message">
                            {showCurrent
                            ? "You don't have any movies currently showing in your watchlist."
                            : "You don't have any upcoming movies in your watchlist."
                        }
                        </p>
                    </div>
                ) : (
                    displayedMovies.map((movie) => (
                        <div className="watchlist__item" key={movie.id || movie.movie_id}>
                            <MovieTile
                                movie={movie}
                                variant="watchlist"
                                onRemovedFromWatchlist={handleMovieRemovedFromWatchlist}
                            />
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default WatchList;
